<template>
  <div class="shell">
    <aside class="nav">
      <div class="brand">
        <span class="mark" aria-hidden="true">HC</span>
        <span class="brand-text">HelmetCare</span>
      </div>

      <nav class="menu">
        <template v-for="group in menuGroups" :key="group.label">
          <div class="group-label">{{ group.label }}</div>
          <router-link
            v-for="item in group.items"
            :key="item.path"
            :to="item.path"
            class="item"
            active-class="active"
          >
            <span class="index" aria-hidden="true">{{ item.index }}</span>
            <span class="label">{{ item.label }}</span>
          </router-link>
        </template>
      </nav>

      <div class="nav-foot">
        <span class="who">{{ authStore.user.value?.email ?? '—' }}</span>
        <div class="foot-actions">
          <button class="ghost" type="button" @click="toggle">
            {{ resolved === 'dark' ? '亮色' : '暗色' }}
          </button>
          <button class="ghost" type="button" @click="handleLogout">退出</button>
        </div>
      </div>
    </aside>

    <div class="main">
      <main class="content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import authStore from '../stores/auth';
import { useTheme } from '../composables/useTheme';

const router = useRouter();
const { resolved, toggle } = useTheme();

/**
 * 十个一级入口平铺会读不出主次，按职责分三组并编号。
 * 编号不是装饰：它同时是页内分区的序号系统，运维之间可以直接说"看 03"。
 */
const menuGroups = [
  {
    label: '运行',
    items: [
      { path: '/overview', label: '总览', index: '01' },
      { path: '/devices', label: '设备与点位', index: '02' },
      { path: '/products', label: '产品中心', index: '12' },
      { path: '/alerts', label: '告警中心', index: '03' },
      { path: '/maintenance', label: '维保与库存', index: '04' },
    ],
  },
  {
    label: '经营',
    items: [
      { path: '/billing', label: '交易与财务', index: '05' },
      { path: '/analytics', label: '数据分析', index: '06' },
      { path: '/operations', label: '小程序运营中心', index: '11' },
    ],
  },
  {
    label: '投放与系统',
    items: [
      { path: '/content', label: '内容与广告', index: '07' },
      { path: '/ota', label: 'OTA', index: '08' },
      { path: '/settings', label: '用户与系统设置', index: '09' },
      { path: '/security', label: 'API 与安全中心', index: '10' },
    ],
  },
];

async function handleLogout(): Promise<void> {
  await authStore.logout();
  await router.push('/login');
}
</script>

<style scoped>
.shell {
  display: flex;
  height: 100vh;
  background: var(--hc-surface-0);
  color: var(--hc-text-1);
  font-family: var(--hc-font-sans);
}

.nav {
  width: 208px;
  flex-shrink: 0;
  background: var(--hc-nav-bg);
  border-right: 1px solid var(--hc-nav-line);
  display: flex;
  flex-direction: column;
  padding: 16px 0 12px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 14px 18px;
}

.mark {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid var(--hc-brand-500);
  color: var(--hc-brand-500);
  font-family: var(--hc-font-mono);
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-text {
  font-family: var(--hc-font-display);
  font-size: 15px;
  letter-spacing: -0.01em;
  color: var(--hc-nav-item-active);
}

.menu {
  flex: 1;
  overflow-y: auto;
  padding: 0 8px;
}

.group-label {
  font-size: 11px;
  letter-spacing: 0.16em;
  color: var(--hc-nav-index);
  padding: 13px 6px 5px;
}

.group-label:first-child {
  padding-top: 0;
}

.item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 5px 6px;
  border-radius: var(--hc-radius-sm);
  color: var(--hc-nav-item);
  text-decoration: none;
  font-size: 14px;
  transition:
    color var(--hc-dur-micro) var(--hc-ease),
    background-color var(--hc-dur-micro) var(--hc-ease);
}

.item:hover {
  color: var(--hc-nav-item-active);
  background: var(--hc-nav-hover-bg);
}

.item.active {
  color: var(--hc-nav-item-active);
  background: var(--hc-nav-active-bg);
}

/* 编号本身就是选中指示：不用左侧粗色条，那几乎从来不像是有意为之 */
.index {
  font-family: var(--hc-font-mono);
  font-size: 10.5px;
  color: var(--hc-nav-index);
  transition: color var(--hc-dur-micro) var(--hc-ease);
}

.item.active .index {
  color: var(--hc-brand-500);
}

.nav-foot {
  padding: 12px 14px 0;
  border-top: 1px solid var(--hc-nav-line);
  margin-top: 8px;
}

.who {
  display: block;
  font-size: 12px;
  color: var(--hc-nav-index);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 8px;
}

.foot-actions {
  display: flex;
  gap: 6px;
}

.ghost {
  flex: 1;
  background: transparent;
  border: 1px solid var(--hc-nav-line);
  color: var(--hc-nav-item);
  border-radius: var(--hc-radius-sm);
  padding: 4px 0;
  font-size: 12.5px;
  cursor: pointer;
  font-family: inherit;
  transition:
    border-color var(--hc-dur-micro) var(--hc-ease),
    color var(--hc-dur-micro) var(--hc-ease);
}

.ghost:hover {
  border-color: var(--hc-brand-500);
  color: var(--hc-nav-item-active);
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 极淡网格底：给质感但不抢戏，纯色底会显得平 */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 18px 26px 34px;
  background-image:
    linear-gradient(var(--hc-grid) 1px, transparent 1px),
    linear-gradient(90deg, var(--hc-grid) 1px, transparent 1px);
  background-size: 30px 30px;
}
</style>
